import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import SideNavbar from '../../components/SideNavbar';
import TopNavbar from '../../components/TopNavbar';
import { db, auth, storage } from '../../firebaseConfig';
import { collection, getDocs, query, orderBy, deleteDoc, addDoc, doc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { v4 as uuidv4 } from 'uuid';
import { useAppSelector } from '../../redux/store';

interface Document {
    id: string;
    name: string;
    documentName?: string;
    uploadTime: Date;
    downloadUrl?: string;
}

const StudentDocUpload = () => {
    const [documents, setDocuments] = useState<Document[]>([]);
    const [pendingDocuments, setPendingDocuments] = useState<Document[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const isCollapsed = useAppSelector(state => state.sidebar.isCollapsed);

    useEffect(() => {
        fetchDocuments();
    }, []);

    const fetchDocuments = async () => {
        try {
            setIsLoading(true);
            const currentUser = auth.currentUser;
            if (!currentUser) return;

            const idTokenResult = await currentUser.getIdTokenResult();
            const companyId = idTokenResult.claims.companyId;
            const leadId = idTokenResult.claims.leadUid;
            
            if (!companyId || !leadId) {
                console.error("Missing required claims");
                return;
            }

            const documentsRef = collection(db, `companies/${companyId}/leads/${leadId}/documents`);
            const documentsQuery = query(documentsRef, orderBy('uploadTime', 'desc'));
            const documentsSnapshot = await getDocs(documentsQuery);
            
            const uploadedDocs: Document[] = [];
            const pendingDocs: Document[] = [];

            for (const doc of documentsSnapshot.docs) {
                const data = doc.data();
                const baseDoc = {
                    id: doc.id,
                    name: data.name,
                    uploadTime: data.uploadTime.toDate(),
                };

                if (data.documentName) {
                    const storageRef = ref(storage, `/student_documents/${companyId}/${leadId}/${data.documentName}`);
                    const downloadUrl = await getDownloadURL(storageRef);
                    uploadedDocs.push({
                        ...baseDoc,
                        documentName: data.documentName,
                        downloadUrl
                    });
                } else {
                    pendingDocs.push(baseDoc);
                }
            }
            
            setDocuments(uploadedDocs);
            setPendingDocuments(pendingDocs);
        } catch (error) {
            console.error("Error fetching documents:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const getFileIcon = (fileName: string) => {
        const extension = fileName.toLowerCase();
        if (extension.endsWith('.pdf')) {
            return "/images/desktop/catppuccin.svg";
        }
        return "/images/desktop/teenyicons.svg"; // for jpg/jpeg
    };

    const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>, document: Document) => {
        try {
            setIsLoading(true);
            const file = event.target.files?.[0];
            if (!file) return;

            const currentUser = auth.currentUser;
            if (!currentUser) return;

            const idTokenResult = await currentUser.getIdTokenResult();
            const companyId = idTokenResult.claims.companyId;
            const leadId = idTokenResult.claims.leadUid;

            if (!companyId || !leadId) {
                console.error("Missing required claims");
                return;
            }

            // Check file size (2MB limit)
            if (file.size > 2 * 1024 * 1024) {
                alert("File size should not exceed 2MB");
                return;
            }

            // Check file type
            const fileType = file.type.toLowerCase();
            if (!['application/pdf', 'image/jpeg', 'image/jpg'].includes(fileType)) {
                alert("Only PDF, JPEG, and JPG files are allowed");
                return;
            }

            // Generate unique filename
            const documentName = `${uuidv4()}_${file.name}`;
            const storageRef = ref(storage, `/student_documents/${companyId}/${leadId}/${documentName}`);
            
            // Upload file to storage
            await uploadBytes(storageRef, file);
            const downloadUrl = await getDownloadURL(storageRef);

            // Update the existing Firestore document
            const docRef = doc(db, `companies/${companyId}/leads/${leadId}/documents/${document.id}`);
            await updateDoc(docRef, {
                documentName: documentName,
                uploadTime: new Date()
            });

            await fetchDocuments(); // Refresh the documents list
        } catch (error) {
            console.error("Error uploading document:", error);
        } finally {
            setIsLoading(false);
            if (event.target) event.target.value = ''; // Reset file input
        }
    };

    const handleUpdateDocument = async (event: React.ChangeEvent<HTMLInputElement>, document: Document) => {
        try {
            setIsLoading(true);
            const file = event.target.files?.[0];
            if (!file || !document.documentName) return;

            const currentUser = auth.currentUser;
            if (!currentUser) return;

            const idTokenResult = await currentUser.getIdTokenResult();
            const companyId = idTokenResult.claims.companyId;
            const leadId = idTokenResult.claims.leadUid;

            if (!companyId || !leadId) {
                console.error("Missing required claims");
                return;
            }

            // Check file size (2MB limit)
            if (file.size > 2 * 1024 * 1024) {
                alert("File size should not exceed 2MB");
                return;
            }

            // Check file type
            const fileType = file.type.toLowerCase();
            if (!['application/pdf', 'image/jpeg', 'image/jpg'].includes(fileType)) {
                alert("Only PDF, JPEG, and JPG files are allowed");
                return;
            }

            // Delete old file from storage
            const oldStorageRef = ref(storage, `/student_documents/${companyId}/${leadId}/${document.documentName}`);
            await deleteObject(oldStorageRef);

            // Upload new file
            const newDocumentName = `${uuidv4()}_${file.name}`;
            const newStorageRef = ref(storage, `/student_documents/${companyId}/${leadId}/${newDocumentName}`);
            await uploadBytes(newStorageRef, file);

            // Update Firestore document
            const docRef = doc(db, `companies/${companyId}/leads/${leadId}/documents/${document.id}`);
            await updateDoc(docRef, {
                documentName: newDocumentName,
                uploadTime: new Date()
            });

            await fetchDocuments(); // Refresh the documents list
        } catch (error) {
            console.error("Error updating document:", error);
        } finally {
            setIsLoading(false);
            if (event.target) event.target.value = ''; // Reset file input
        }
    };

    const handleDeleteDocument = async (document: Document) => {
        if (!window.confirm('Are you sure you want to delete this document?') || !document.documentName) return;
        
        try {
            setIsLoading(true);
            const currentUser = auth.currentUser;
            if (!currentUser) return;

            const idTokenResult = await currentUser.getIdTokenResult();
            const companyId = idTokenResult.claims.companyId;
            const leadId = idTokenResult.claims.leadUid;

            if (!companyId || !leadId) {
                console.error("Missing required claims");
                return;
            }

            // Delete file from storage
            const storageRef = ref(storage, `/student_documents/${companyId}/${leadId}/${document.documentName}`);
            await deleteObject(storageRef);

            // Delete document from Firestore
            const docRef = doc(db, `companies/${companyId}/leads/${leadId}/documents/${document.id}`);
            await deleteDoc(docRef);

            await fetchDocuments(); // Refresh the documents list
        } catch (error) {
            console.error("Error deleting document:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const formatUploadTime = (date: Date) => {
        const now = new Date();
        const diffTime = Math.abs(now.getTime() - date.getTime());
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        const timeString = date.toLocaleTimeString('en-US', { 
            hour: '2-digit', 
            minute: '2-digit', 
            hour12: true 
        });
        const dateString = date.toLocaleDateString('en-US', { 
            day: '2-digit', 
            month: 'short'
        });
        
        return `${dateString}, ${timeString} · ${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
    };

    return (
        <div className="min-h-screen bg-gray-200">
            {/* <div className="fixed top-0 left-0 w-full z-10">
                <TopNavbar />
            </div> */}
            <div className="fixed top-16 left-0 h-full z-10">
                <SideNavbar />
            </div>

            <div className={`${isCollapsed ? 'ml-[70px]' : 'ml-[290px]'} mt-[109px] p-8 transition-all duration-300`}>
                <div className="bg-white p-6 rounded-3xl shadow-md w-full min-h-screen relative">
                    {isLoading && (
                        <div className="absolute inset-0 bg-white/80 rounded-3xl flex items-center justify-center z-50">
                            <div className="flex flex-col items-center">
                                <div className="w-12 h-12 border-4 border-cyan-600 border-t-transparent rounded-full animate-spin"></div>
                                <p className="mt-4 text-gray-600 font-montserrat">Loading documents...</p>
                            </div>
                        </div>
                    )}
                    <div className="flex items-center gap-6 mb-3">
                        <div
                            className="flex items-center justify-center w-14 h-14 rounded-full"
                            style={{ background: 'linear-gradient(180deg, #00A6D9 10%, #3483BE 100%)' }}
                        >
                            <img
                                src={`${process.env.PUBLIC_URL}/images/desktop/iconoir_profile-circle.svg`}
                                alt="Ticket Icon"
                                className="w-8 h-8"
                            />
                        </div>
                        <h2 className="font-montserrat text-2xl font-medium text-black">Document Upload</h2>
                    </div>
                    <div className='px-8'>
                        <h3 className="font-montserrat text-lg font-medium text-gray-900 mt-5 mb-4">Pending for Upload </h3>
                        
                        <div className="w-3/4 mb-8 space-y-8">
                            {pendingDocuments.map((doc) => (
                                <div key={doc.id} className="relative">
                                    <label className="block font-montserrat font-normal text-base text-black mb-2">
                                        {doc.name}
                                    </label>
                                    <div className="relative w-full">
                                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2">
                                            <img
                                                src={`${process.env.PUBLIC_URL}/images/desktop/teenyicons.svg`}
                                                alt="icon"
                                                className="w-8 h-8 text-gray-400"
                                            />
                                        </span>
                                        <div className="absolute left-16 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm pointer-events-none">
                                            Upload Document
                                        </div>
                                        <div className="absolute right-6 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm">
                                            PDF, JPG or JPEG, max 2 MB
                                        </div>
                                        <input
                                            type="file"
                                            onChange={(e) => handleFileUpload(e, doc)}
                                            className="w-full py-3 pl-14 pr-4 bg-white border border-cyan-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-sky-950 opacity-0 cursor-pointer"
                                            accept=".pdf,.jpg,.jpeg"
                                            disabled={isLoading}
                                        />
                                        <div className="absolute inset-0 pointer-events-none border border-cyan-600 rounded-2xl"></div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="w-3/4">
                            <h2 className="mt-6 font-montserrat text-lg font-medium text-gray-900 mb-3">
                                Uploaded Documents
                            </h2>

                            <div className="space-y-8">
                                {documents.map((doc) => (
                                    <div key={doc.id} className="relative">
                                        <label className="block font-montserrat font-normal text-base text-black mb-2">
                                            {doc.name}
                                        </label>
                                        <div className="relative w-full">
                                            <span className="absolute left-3 top-1/2 transform -translate-y-1/2">
                                                <img
                                                    src={`${process.env.PUBLIC_URL}${doc.documentName ? getFileIcon(doc.documentName) : '/images/desktop/catppuccin.svg'}`}
                                                    alt="icon"
                                                    className="w-8 h-8 text-gray-400"
                                                />
                                            </span>
                                            <div 
                                                className="absolute left-16 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm pointer-events-none"
                                                onClick={() => doc.downloadUrl && window.open(doc.downloadUrl, '_blank')}
                                            >
                                                {doc.documentName && doc.documentName.split('_').slice(1).join('_')}<br />
                                                <span className="text-gray-500">
                                                    {formatUploadTime(doc.uploadTime)}
                                                </span>
                                            </div>
                                            
                                            <div 
                                                className="w-full py-3 pl-14 pr-4 bg-white border border-cyan-600 rounded-xl cursor-pointer"
                                                onClick={() => doc.downloadUrl && window.open(doc.downloadUrl, '_blank')}
                                            >
                                                &nbsp;
                                            </div>
                                            <div className="absolute inset-0 pointer-events-none border border-cyan-600 rounded-2xl"></div>
                                        </div>
                                        
                                        <div className="absolute top-2/3 -right-20 transform -translate-y-1/2 flex items-center gap-4">
                                            <label className={`cursor-pointer ${isLoading ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                                                <img
                                                    src={`${process.env.PUBLIC_URL}/images/desktop/blue-edit-pen.svg`}
                                                    alt="Edit"
                                                    className="w-5 h-5"
                                                />
                                                <input
                                                    type="file"
                                                    onChange={(e) => handleUpdateDocument(e, doc)}
                                                    className="hidden"
                                                    accept=".pdf,.jpg,.jpeg"
                                                    disabled={isLoading}
                                                />
                                            </label>
                                            <img
                                                src={`${process.env.PUBLIC_URL}/images/desktop/deleteicon.svg`}
                                                alt="Delete"
                                                className="w-5 h-5 cursor-pointer"
                                                onClick={() => handleDeleteDocument(doc)}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                    <hr className="mt-12 w-full"
                        style={{ border: "none", height: "1px", background: "linear-gradient(to right, #FFFFFF 0%, #72AAD2 33%, #5E9DCB 66%, #FFFFFF 100%)"}}/>
                </div>
            </div>
        </div>
    );
}

export default StudentDocUpload;